import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Link as ChakraLink,
  Icon,
  Spacer,
  Button,
  Text,
  Avatar,
  Divider,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiBox, FiBook, FiLogOut, FiKey, FiShield, FiClipboard, FiServer, FiTrendingUp, FiHeart, FiMenu } from 'react-icons/fi';

import Applications from './components/Applications';
import ApiKeys from './components/ApiKeys';
import SuperAdmin from './components/SuperAdmin';
import AuditLog from './components/AuditLog';
import ApiDocs from './components/ApiDocs';
import PostgresStatus from './components/PostgresStatus';
import UsageAnalytics from './components/UsageAnalytics';
import HealthCheck from './components/HealthCheck';
import LoginForm from './components/Login';

const NavButton = ({ to, icon, children, isActive }) => (
  <ChakraLink as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      width="full"
      justifyContent="flex-start"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'blue' : 'whiteAlpha'}
      color={isActive ? 'white' : 'whiteAlpha.800'}
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      _hover={{
        bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight={isActive ? '600' : '400'}
    >
      {children}
    </Button>
  </ChakraLink>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => { onClose(); }, [location.pathname, onClose]);

  const handleLogin = () => {
    setToken(localStorage.getItem('token'));
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const renderSidebarContent = () => (
    <>
      {/* Logo/Brand */}
      <Flex align="center" px={4} mb={6}>
        <Box w={12} h={12} borderRadius="full" overflow="hidden" bg="white" p={0.5}>
          <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </Box>
        <Box ml={3}>
          <Text fontSize="lg" fontWeight="bold" color="white">Registry</Text>
        </Box>
      </Flex>

      <Divider borderColor="whiteAlpha.300" mb={4} />

      {/* Navigation */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
        Management
      </Text>
      <NavButton to="/" icon={FiBox} isActive={location.pathname === '/'}>
        Applications
      </NavButton>
      <NavButton to="/api-keys" icon={FiKey} isActive={location.pathname === '/api-keys'}>
        API Keys
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Administration
      </Text>
      <NavButton to="/super-admin" icon={FiShield} isActive={location.pathname === '/super-admin'}>
        Super Admin
      </NavButton>
      <NavButton to="/audit-log" icon={FiClipboard} isActive={location.pathname === '/audit-log'}>
        Audit Log
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Insights
      </Text>
      <NavButton to="/analytics" icon={FiTrendingUp} isActive={location.pathname === '/analytics'}>
        Usage Analytics
      </NavButton>
      <NavButton to="/health" icon={FiHeart} isActive={location.pathname === '/health'}>
        Health Check
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Infrastructure
      </Text>
      <NavButton to="/postgres" icon={FiServer} isActive={location.pathname === '/postgres'}>
        PostgreSQL Status
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Developer
      </Text>
      <NavButton to="/api" icon={FiBook} isActive={location.pathname === '/api'}>
        API Documentation
      </NavButton>

      <Spacer />

      {/* User Section */}
      <Divider borderColor="whiteAlpha.300" />
      <Flex align="center" px={4} py={3}>
        <Avatar size="sm" bg="blue.400" />
        <Box ml={3} flex={1}>
          <Text fontSize="sm" color="white" fontWeight="medium">Admin User</Text>
          <Text fontSize="xs" color="whiteAlpha.600">Logged in</Text>
        </Box>
      </Flex>
      <Button
        leftIcon={<Icon as={FiLogOut} />}
        onClick={handleLogout}
        width="full"
        justifyContent="flex-start"
        variant="ghost"
        color="whiteAlpha.800"
        _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
        borderRadius="lg"
      >
        Logout
      </Button>
    </>
  );

  return (
    <Flex direction="column" height="100vh" bg="gray.50">
      {/* Mobile top bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        flexShrink={0}
        height="56px"
        bg="cyan.700"
        align="center"
        px={4}
        justify="space-between"
        boxShadow="md"
      >
        <IconButton
          icon={<Icon as={FiMenu} />}
          onClick={onOpen}
          variant="ghost"
          color="white"
          aria-label="Open navigation"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
        <Text color="white" fontWeight="bold" fontSize="md">Registry</Text>
        <IconButton
          icon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          variant="ghost"
          color="white"
          aria-label="Logout"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Flex>

      {/* Desktop layout row */}
      <Flex flex="1" overflow="hidden">
        {/* Desktop Sidebar */}
        <VStack
          as="nav"
          display={{ base: 'none', md: 'flex' }}
          width="280px"
          py={6}
          px={4}
          bgGradient="linear(to-b, cyan.600, cyan.800)"
          spacing={2}
          align="stretch"
          boxShadow="xl"
          overflowY="auto"
          flexShrink={0}
        >
          {renderSidebarContent()}
        </VStack>

        {/* Main Content */}
        <Box flex="1" overflowY="auto" p={{ base: 4, md: 8 }}>
          <Routes>
            <Route path="/" element={<Applications />} />
            <Route path="/api-keys" element={<ApiKeys />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/postgres" element={<PostgresStatus />} />
            <Route path="/analytics" element={<UsageAnalytics />} />
            <Route path="/health" element={<HealthCheck />} />
            <Route path="/api" element={<ApiDocs />} />
          </Routes>
        </Box>
      </Flex>

      {/* Mobile nav drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent bgGradient="linear(to-b, cyan.600, cyan.800)" maxW="280px">
          <DrawerCloseButton color="white" top={4} />
          <DrawerBody p={0}>
            <VStack spacing={2} align="stretch" py={6} px={4} minH="100%">
              {renderSidebarContent()}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default App;